import { Viewer } from '@/components/editor/Viewer';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useQuery } from '@tanstack/react-query';
import { ticketQueries } from '@/features/tickets/tickets.queries';
import {
  CHANGELOG,
  CHANGELOG_REF_PROJECT_ID,
  FEEDBACK_LOOP_TICKET_ID,
} from '@/config/version';
import { Link } from '@tanstack/react-router';
import { useCreateTicketStore } from '@/components/editor/TicketPlugin/TicketPlugin';
import { TicketsStoreProvider } from '@/components/editor/TicketPlugin/TicketsStoreProvider';
import { useState } from 'react';

interface ChangelogViewerProps {
  trigger?: React.ReactNode;
  /** Changelog markdown override — e.g. a waiting SW's release notes (the
   * imported CHANGELOG constant is this build's own). */
  value?: string;
  /** Controlled mode (no trigger rendered) — used by the update flow. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function ChangelogViewer({
  trigger,
  value,
  open: openProp,
  onOpenChange,
}: ChangelogViewerProps) {
  const [openState, setOpenState] = useState(false);
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : openState;
  const setOpen = (next: boolean) => {
    if (!controlled) {
      setOpenState(next);
    }
    onOpenChange?.(next);
  };

  // Only fetch tickets when dialog is open. Uses the shared project-tickets
  // query so ticket mutations (close, reopen, ...) invalidate it (#3514).
  const { data: projectTickets = [], isLoading } = useQuery({
    ...ticketQueries.projectTickets(CHANGELOG_REF_PROJECT_ID),
    enabled: open,
  });

  const ticketsStore = useCreateTicketStore(projectTickets, isLoading);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!controlled && (
        <DialogTrigger asChild>
          {trigger || (
            <button className="text-muted-foreground hover:text-foreground text-xs underline">
              View Changelog
            </button>
          )}
        </DialogTrigger>
      )}
      <DialogContent className="flex max-h-[90vh] max-w-3xl flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>Release notes</DialogTitle>
          <DialogDescription>
            Auto-generated from commit messages. To get involved with
            development, check out and join the{' '}
            {FEEDBACK_LOOP_TICKET_ID ? (
              <Link
                to="/tickets/$ticketId"
                params={{ ticketId: FEEDBACK_LOOP_TICKET_ID }}
                className="underline"
              >
                certhon.app feedback loop
              </Link>
            ) : (
              <span className="underline">Development feedback loop</span>
            )}
            .
          </DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto pr-4">
          {open && (
            <TicketsStoreProvider store={ticketsStore}>
              <Viewer
                value={value ?? CHANGELOG}
                className="rounded-none border-none shadow-none"
              />
            </TicketsStoreProvider>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
